import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleVenueData } from '../../../shared/dto/EntityRepository';
import {
  isCtrlOrMetaClick,
  isTypingKeyTarget,
  nextWorkIndexForArrow,
} from '../../../shared/hooks/workListSelectionUtils';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { runAfterUnsavedVenueCheck } from '../../component/venues/runAfterUnsavedVenueCheck';
import { useAdminVenueStore } from '../store/adminVenueStore';

interface UseAdminVenueListSelectionOptions {
  venueList: SimpleVenueData[];
  selectVenue: (venue: SimpleVenueData) => Promise<void>;
  selectVenues: (venues: SimpleVenueData[]) => Promise<void>;
  onSelectionStart?: () => void;
  disabled?: boolean;
}

export const useAdminVenueListSelection = ({
  venueList,
  selectVenue,
  selectVenues,
  onSelectionStart,
  disabled = false,
}: UseAdminVenueListSelectionOptions) => {
  const [selectionAnchorId, setSelectionAnchorId] = useState('');
  const [selectionEndId, setSelectionEndId] = useState('');
  const activeVenueId = useAdminVenueStore((state) =>
    state.activeVenue?.id ? String(state.activeVenue.id) : ''
  );
  const multiSelectedVenueIdsKey = useAdminVenueStore((state) =>
    state.multiSelectedVenueIds.join('|')
  );
  const venueListRef = useRef(venueList);
  const selectVenueRef = useRef(selectVenue);
  const selectVenuesRef = useRef(selectVenues);
  const onSelectionStartRef = useRef(onSelectionStart);

  venueListRef.current = venueList;
  selectVenueRef.current = selectVenue;
  selectVenuesRef.current = selectVenues;
  onSelectionStartRef.current = onSelectionStart;

  useEffect(() => {
    const multiIds = multiSelectedVenueIdsKey
      ? multiSelectedVenueIdsKey.split('|')
      : [];
    if (multiIds.length > 1) {
      setSelectionAnchorId((current) =>
        current && multiIds.includes(current) ? current : multiIds[0]
      );
      setSelectionEndId((current) =>
        current && multiIds.includes(current) ? current : multiIds[multiIds.length - 1]
      );
    } else if (activeVenueId) {
      setSelectionAnchorId(activeVenueId);
      setSelectionEndId(activeVenueId);
    } else {
      setSelectionAnchorId('');
      setSelectionEndId('');
    }
  }, [activeVenueId, multiSelectedVenueIdsKey]);

  const commitSelection = useCallback((
    venues: SimpleVenueData[],
    anchorId: string,
    endId: string,
  ) => {
    runAfterUnsavedVenueCheck(() => {
      onSelectionStartRef.current?.();
      setSelectionAnchorId(anchorId);
      setSelectionEndId(endId);
      if (venues.length === 0) {
        useAdminVenueStore.getState().clearVenueSelection();
      } else if (venues.length === 1) {
        void selectVenueRef.current(venues[0]);
      } else {
        void selectVenuesRef.current(venues);
      }
    });
  }, []);

  useEffect(() => {
    if (selectionAnchorId && !venueList.some((venue) => String(venue.id) === selectionAnchorId)) {
      setSelectionAnchorId('');
    }
    if (selectionEndId && !venueList.some((venue) => String(venue.id) === selectionEndId)) {
      setSelectionEndId('');
    }
  }, [selectionAnchorId, selectionEndId, venueList]);

  const applyShiftRange = useCallback((endVenue: SimpleVenueData) => {
    const list = venueListRef.current;
    const { activeVenue, multiSelectedVenueIds } = useAdminVenueStore.getState();
    const anchorId = (
      selectionAnchorId && list.some((venue) => String(venue.id) === selectionAnchorId)
        ? selectionAnchorId
        : String(activeVenue?.id || multiSelectedVenueIds[0] || endVenue.id)
    );
    const endVenueId = String(endVenue.id);
    const anchorIndex = list.findIndex((venue) => String(venue.id) === anchorId);
    const endIndex = list.findIndex((venue) => String(venue.id) === endVenueId);
    if (anchorIndex < 0 || endIndex < 0) {
      commitSelection([endVenue], endVenueId, endVenueId);
      return;
    }

    const rangeIds = new Set(
      list
        .slice(Math.min(anchorIndex, endIndex), Math.max(anchorIndex, endIndex) + 1)
        .map((venue) => String(venue.id)),
    );
    const selectedIds = new Set(multiSelectedVenueIds);
    const next = multiSelectedVenueIds.length > 0
      ? list.filter((venue) => selectedIds.has(String(venue.id)) || rangeIds.has(String(venue.id)))
      : list.filter((venue) => rangeIds.has(String(venue.id)));
    commitSelection(next, anchorId, endVenueId);
  }, [commitSelection, selectionAnchorId]);

  const handleVenueClick = useCallback((
    venue: SimpleVenueData,
    event: MouseEvent<HTMLElement>,
  ) => {
    if (disabled) return;
    if (event.shiftKey) {
      applyShiftRange(venue);
      return;
    }

    const list = venueListRef.current;
    const { activeVenue, multiSelectedVenueIds } = useAdminVenueStore.getState();
    const venueId = String(venue.id);
    const isInMulti = multiSelectedVenueIds.includes(venueId);

    if (multiSelectedVenueIds.length > 0) {
      if (!isInMulti) {
        if (isCtrlOrMetaClick(event)) {
          const nextIds = new Set([...multiSelectedVenueIds, venueId]);
          commitSelection(
            list.filter((item) => nextIds.has(String(item.id))),
            venueId,
            venueId,
          );
        } else {
          commitSelection([venue], venueId, venueId);
        }
        return;
      }

      const nextIds = new Set(multiSelectedVenueIds.filter((id) => id !== venueId));
      const next = list.filter((item) => nextIds.has(String(item.id)));
      const nextId = next[0] ? String(next[0].id) : '';
      commitSelection(next, nextId, nextId);
      return;
    }

    if (
      activeVenue?.id
      && String(activeVenue.id) !== venueId
      && isCtrlOrMetaClick(event)
    ) {
      const activeFromList = list.find((item) =>
        String(item.id) === String(activeVenue.id)
      );
      if (activeFromList) {
        const activeId = String(activeFromList.id);
        const ids = new Set([activeId, venueId]);
        commitSelection(
          list.filter((item) => ids.has(String(item.id))),
          activeId,
          venueId,
        );
        return;
      }
    }

    if (String(activeVenue?.id ?? '') === venueId) return;
    commitSelection([venue], venueId, venueId);
  }, [applyShiftRange, commitSelection, disabled]);

  const commitMarqueeVenueIds = useCallback((venueIds: string[]) => {
    if (disabled || venueIds.length === 0) return;
    const idSet = new Set(venueIds);
    const venues = venueListRef.current.filter((venue) => idSet.has(String(venue.id)));
    if (venues.length === 0) return;
    commitSelection(
      venues,
      String(venues[0].id),
      String(venues[venues.length - 1].id),
    );
  }, [commitSelection, disabled]);

  const handleVenueListBackgroundClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-venue-id]')) return;
    if (target.closest('[data-suppress-marquee]')) return;

    const { multiSelectedVenueIds } = useAdminVenueStore.getState();
    if (multiSelectedVenueIds.length < 2) return;
    const first = venueListRef.current.find(
      (venue) => String(venue.id) === multiSelectedVenueIds[0],
    );
    if (first) commitSelection([first], String(first.id), String(first.id));
  }, [commitSelection, disabled]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (disabled || useModalStore.getState().standardModal !== null) return;
    if (isTypingKeyTarget(event.target)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    const list = venueListRef.current;
    if (list.length === 0) return;
    const { activeVenue, multiSelectedVenueIds } = useAdminVenueStore.getState();

    if (event.shiftKey) {
      const focusId = selectionEndId
        || (event.key === 'ArrowUp'
          ? multiSelectedVenueIds[0]
          : multiSelectedVenueIds[multiSelectedVenueIds.length - 1])
        || activeVenue?.id
        || '';
      const focusIndex = list.findIndex((venue) => String(venue.id) === focusId);
      const nextIndex = nextWorkIndexForArrow(focusIndex, event.key, list.length, 1);
      if (nextIndex == null) return;
      const anchorId = (
        selectionAnchorId && list.some((venue) => String(venue.id) === selectionAnchorId)
          ? selectionAnchorId
          : String(activeVenue?.id || multiSelectedVenueIds[0] || list[nextIndex].id)
      );
      const anchorIndex = list.findIndex((venue) => String(venue.id) === anchorId);
      if (anchorIndex < 0) return;
      event.preventDefault();
      commitSelection(
        list.slice(
          Math.min(anchorIndex, nextIndex),
          Math.max(anchorIndex, nextIndex) + 1,
        ),
        anchorId,
        String(list[nextIndex].id),
      );
      return;
    }

    const activeId = String(activeVenue?.id || multiSelectedVenueIds[0] || '');
    const activeIndex = list.findIndex((venue) => String(venue.id) === activeId);
    const nextIndex = nextWorkIndexForArrow(activeIndex, event.key, list.length, 1);
    if (nextIndex == null) return;
    event.preventDefault();
    const nextId = String(list[nextIndex].id);
    commitSelection([list[nextIndex]], nextId, nextId);
  }, [commitSelection, disabled, selectionAnchorId, selectionEndId]);

  return {
    handleVenueClick,
    handleVenueListBackgroundClick,
    handleKeyDown,
    commitMarqueeVenueIds,
  };
};
